import { Badge } from '@amore/ui/components/badge'
import { Button, buttonStyles } from '@amore/ui/components/button'
import { Card, CardHeader, CardTitle } from '@amore/ui/components/card'
import { Link, useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import { ArrowRight, CloudSun, MapPin, Plus, Wrench } from 'lucide-react'
import { useRef } from 'react'
import { supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import { useCanEdit, useWedding } from '@/mock/store'
import type { CeremonyPreparation } from '@/mock/types'
import { type Column, DataTable } from '@/patterns/data-table'
import { EmptyState } from '@/patterns/empty-state'
import { ScreenSection } from '@/patterns/screen'
import { CeremonyEditAction } from './edit-action'
import { CeremonyMissingSheet } from './missing-sheet'
import { ceremonyPerson, ceremonySummary } from './model'
import { CeremonyPersonIdentity } from './person-identity'
import { CeremonyPlaceSheet } from './place-sheet'
import { CeremonyPlanBSheet } from './plan-b-sheet'
import { CeremonyPreparationSheet } from './preparation-sheet'

export function CeremonyPlacePage() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const navigate = useNavigate()
  const { preparationId } = useParams({ strict: false })
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const addButton = useRef<HTMLButtonElement>(null)
  const preparation = data.ceremony.preparations.find((item) => item.id === preparationId)
  const closeDetail = (id: string) => {
    void navigate({ to: '/ceremony/place' })
    requestAnimationFrame(() => {
      ;(
        document.getElementById(id) ??
        addButton.current ??
        document.querySelector<HTMLAnchorElement>(
          'nav[aria-label="Stránky obřadu"] a[href="/ceremony/place"]',
        )
      )?.focus({ preventScroll: true })
    })
  }
  const openPreparation = (item: CeremonyPreparation) =>
    void navigate({ to: `/ceremony/place/preparations/${item.id}` })
  const { event, venue, planBDecider } = ceremonySummary(data)
  const { planB, preparations } = data.ceremony
  const venueRecord = data.venues.find(
    (item) => item.source.type === 'supplier' && item.source.supplierId === venue?.id,
  )
  const columns: Column<CeremonyPreparation>[] = [
    {
      key: 'preparation',
      header: 'Domluva',
      width: 'minmax(0,1fr)',
      primary: true,
      cell: (item) => (
        <div className="min-w-0 space-y-1">
          <p className="break-words font-semibold">{item.title}</p>
          {item.description ? (
            <p className="break-words text-sm leading-6 font-normal text-muted-foreground">
              {item.description}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'person',
      header: 'Má na starosti',
      width: '15rem',
      interactive: true,
      cell: (item) => {
        const person = ceremonyPerson(item.person, data)
        return (
          <CeremonyPersonIdentity
            name={person?.name}
            to={person && 'to' in person ? person.to : undefined}
            empty="Zatím nepřiřazeno"
            compact
          />
        )
      },
    },
  ]

  return (
    <>
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Card padding="none" className="overflow-hidden">
          <CardHeader appearance="strip">
            <div className="flex items-center gap-2.5">
              <MapPin className="size-4 text-action" aria-hidden="true" />
              <CardTitle as="h2">Místo obřadu</CardTitle>
            </div>
            <CeremonyEditAction
              id="ceremony-edit-place"
              label="Upravit místo obřadu"
              onClick={() => void navigate({ to: '/ceremony/place/details' })}
            />
          </CardHeader>
          <div className="space-y-4 p-5">
            <div className="space-y-2">
              <h3 className="break-words font-heading text-2xl leading-tight">
                {event?.location || venue?.name || 'Místo zatím není vybrané'}
              </h3>
              {venue ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/suppliers/${venue.id}`}
                    className="text-sm font-semibold text-action hover:underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    {venue.name}
                  </Link>
                  <Badge tone={supplierStatusTone(venue.status)}>
                    {supplierStatusLabels[venue.status]}
                  </Badge>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Vyberte místo, až budete mít jasno. Konkrétní prostor můžete doplnit samostatně.
                </p>
              )}
            </div>
            {venueRecord ? (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                <p className="text-xs text-muted-foreground">
                  Příjezd, parkování a zázemí najdete u místa.
                </p>
                <Link
                  to={`/venues/${venueRecord.id}`}
                  className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
                >
                  Otevřít místo <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </div>
            ) : null}
          </div>
        </Card>
        <Card padding="none" className="overflow-hidden">
          <CardHeader appearance="strip">
            <div className="flex items-center gap-2.5">
              <CloudSun className="size-4 text-action" aria-hidden="true" />
              <CardTitle as="h2">Plán B</CardTitle>
            </div>
            <CeremonyEditAction
              id="ceremony-edit-plan-b"
              label="Upravit plán B"
              onClick={() => void navigate({ to: '/ceremony/place/plan-b' })}
            />
          </CardHeader>
          <div className="space-y-4 p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">Kdyby počasí nepřálo</p>
              <Badge
                tone={planB?.location && planB.decisionTime && planBDecider ? 'success' : 'neutral'}
              >
                {planB?.location && planB.decisionTime && planBDecider
                  ? 'Domluveno'
                  : planB
                    ? 'Ještě doladit'
                    : 'Zatím otevřené'}
              </Badge>
            </div>
            <dl className="divide-y divide-border text-sm">
              {[
                ['Náhradní místo', planB?.location || 'Zatím nevybrané'],
                ['Kdo rozhodne', planBDecider?.name || 'Zatím neurčeno'],
                ['Kdy rozhodne', planB?.decisionTime || 'Zatím neurčeno'],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex flex-wrap justify-between gap-x-4 gap-y-1 py-3 first:pt-0 last:pb-0"
                >
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="min-w-0 break-words font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Card>
      </div>
      <ScreenSection
        title="Příprava místa"
        description="Co má být na místě nachystané a kdo to zajistí. Jen domluvy, které potřebujete."
        action={
          canEdit ? (
            <Button
              ref={addButton}
              onClick={() => void navigate({ to: '/ceremony/place/preparations/new' })}
            >
              <Plus className="size-4" aria-hidden="true" />
              Přidat domluvu
            </Button>
          ) : undefined
        }
      >
        {preparations.length ? (
          <DataTable
            aria-label="Domluvy k přípravě místa"
            columns={columns}
            rows={preparations}
            getRowId={(item) => item.id}
            onRowClick={canEdit ? openPreparation : undefined}
            rowActions={
              canEdit
                ? (item) => (
                    <CeremonyEditAction
                      id={`ceremony-preparation-${item.id}`}
                      label={`Upravit domluvu ${item.title}`}
                      onClick={() => openPreparation(item)}
                    />
                  )
                : undefined
            }
          />
        ) : (
          <EmptyState
            icon={<Wrench />}
            title="Prostor pro vaše domluvy"
            description={
              canEdit
                ? 'Rozmístění židlí, ozvučení nebo cokoli dalšího. Přidejte to, co je pro váš obřad důležité.'
                : 'K přípravě místa zatím nejsou zapsané žádné domluvy.'
            }
          />
        )}
      </ScreenSection>
      {pathname === '/ceremony/place/details' ? (
        <CeremonyPlaceSheet onClose={() => closeDetail('ceremony-edit-place')} />
      ) : null}
      {pathname === '/ceremony/place/plan-b' ? (
        <CeremonyPlanBSheet onClose={() => closeDetail('ceremony-edit-plan-b')} />
      ) : null}
      {preparationId === 'new' || preparation ? (
        <CeremonyPreparationSheet
          key={preparationId}
          item={preparation}
          onClose={() => closeDetail(`ceremony-preparation-${preparationId}`)}
        />
      ) : preparationId ? (
        <CeremonyMissingSheet
          title="Domluva už tu není"
          onClose={() => closeDetail('ceremony-add-preparation')}
        />
      ) : null}
    </>
  )
}
